import { getCurrentVersionQueryKey, getVersionsQueryKey } from "@synthesis/shared-services/internal/application/hooks";
import type {
  FinalDecisionAuthorDto,
  FinalDecisionRemarkDto,
  FinalDecisionVersionDtoApprovalStatusEnumKey,
  FinalDecisionVersionDtoDecisionEnumKey,
  InternalRequestDetailResponseFinalDecisionEnumKey,
} from "@synthesis/shared-services/internal/application/types";
import type { QueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

import type { ApplicationStatus } from "../../../../constants";
import { REJECT_DECISION_ATTACHMENT_DESCRIPTION } from "../application-control-reject-modal.lib";

// ---------------------------------- ТИПЫ ----------------------------------

export type FinalDecisionValue = InternalRequestDetailResponseFinalDecisionEnumKey;

// ---------------------------------- КОНСТАНТЫ ----------------------------------

export const FINAL_DECISION_RESULT_OPTIONS: ReadonlyArray<{
  value: FinalDecisionValue;
  label: string;
}> = [
  { value: "APPROVED", label: "Положительное решение" },
  { value: "REJECTED", label: "Мотивированный отказ" },
] as const;

export const FINAL_DECISION_CREATE_SNACK_MESSAGES = {
  pending: "Выполняется загрузка...",
  fulfilled: "Итоговое решение загружено",
  rejected: "Не удалось загрузить итоговое решение. Попробуйте еще раз",
} as const;

export const FINAL_DECISION_UPDATE_SNACK_MESSAGES = {
  pending: "Выполняется обновление решения...",
  fulfilled: "Итоговое решение обновлено",
  rejected: "Не удалось обновить итоговое решение. Попробуйте еще раз",
} as const;

/** UI/API-режим модалки итогового решения. */
export type FinalDecisionModalMode = "create" | "edit" | "add-version";

export const FINAL_DECISION_MODAL_TITLE: Record<FinalDecisionModalMode, string> = {
  create: "Загрузка результата рассмотрения",
  edit: "Редактирование результата рассмотрения",
  "add-version": "Загрузка результата рассмотрения",
} as const;

export const FINAL_DECISION_FORM_TITLE: Record<FinalDecisionModalMode, string> = {
  create: "Загрузите итоговое решение",
  edit: "Редактирование итогового решения",
  "add-version": "Загрузите итоговое решение",
} as const;

export const FINAL_DECISION_SUBMIT_LABEL: Record<FinalDecisionModalMode, string> = {
  create: "Загрузить",
  edit: "Сохранить",
  "add-version": "Загрузить",
} as const;

/**
 * POST /final-decision — создать черновик, когда его ещё нет.
 * Допустимо в DECISION_FORMING и DECISION_REWORK: после BAC-2324 на доработке
 * предыдущий черновик удалён, для начала доработки нужен новый POST; PUT — только
 * когда черновик уже есть. ACCESS_DENIED на GET ≠ отсутствие черновика.
 */
export const shouldCreateFinalDecisionDraft = (params: { status?: ApplicationStatus; hasDraft: boolean; isDraftAccessDenied?: boolean }): boolean => {
  if (params.hasDraft || params.isDraftAccessDenied) {
    return false;
  }

  return params.status === "DECISION_FORMING" || params.status === "DECISION_REWORK";
};

export const FINAL_DECISION_REMARK_CREATE_SNACK_MESSAGES = {
  pending: "Выполняется отправка...",
  fulfilled: "Заявка отправлена на доработку",
  rejected: "Не удалось добавить замечание",
} as const;

export const FINAL_DECISION_REMARK_CREATE_SNACK_DESCRIPTION = {
  pending: "",
  fulfilled: "",
  rejected: "Попробуйте еще раз",
} as const;

export const FINAL_DECISION_REMARK_DELETE_SNACK_MESSAGES = {
  pending: "Удаление замечания...",
  fulfilled: "Замечание удалено",
  rejected: "Не удалось удалить замечание",
} as const;

/** Статусы, достижимые после отправки итогового решения на согласование (до них версии нет). */
export const FINAL_DECISION_VERSION_STATUSES: readonly ApplicationStatus[] = [
  "NTP_APPROVAL",
  "NG_APPROVAL",
  "DECISION_REWORK",
  "TU_ISSUED",
  "REJECTED",
] as const;

export const isFinalDecisionVersionStatus = (status?: ApplicationStatus): boolean =>
  status !== undefined && (FINAL_DECISION_VERSION_STATUSES as readonly ApplicationStatus[]).includes(status);

/** Подпись статуса согласования текущей версии (approvalStatus из GET current-version). */
export const FINAL_DECISION_APPROVAL_STATUS_LABELS: Partial<Record<FinalDecisionVersionDtoApprovalStatusEnumKey, string>> = {
  NTP_APPROVAL: "На согласовании",
  NG_APPROVAL: "На согласовании",
  DECISION_REWORK: "Возвращено",
  TU_ISSUED: "Согласовано",
  REJECTED: "Отклонено",
} as const;

export const getFinalDecisionApprovalStatusLabel = (approvalStatus?: FinalDecisionVersionDtoApprovalStatusEnumKey | null): string => {
  if (!approvalStatus) {
    return "—";
  }

  return FINAL_DECISION_APPROVAL_STATUS_LABELS[approvalStatus] ?? approvalStatus;
};

export const formatFinalDecisionSubmittedAt = (submittedAt?: string | null): string =>
  submittedAt ? dayjs(submittedAt).format("DD.MM.YYYY, HH:mm") : "—";

export const formatFinalDecisionDate = (value?: string | null): string => (value ? dayjs(value).format("DD.MM.YYYY") : "—");

export const getFinalDecisionDecisionTypeLabel = (decision?: FinalDecisionVersionDtoDecisionEnumKey | null): string => {
  const option = FINAL_DECISION_RESULT_OPTIONS.find((item) => item.value === decision);

  return option?.label ?? "—";
};

// ---------------------------------- УТИЛИТЫ ----------------------------------

/**
 * Признак «легаси»-вложения черновика итогового решения: до появления выделенного
 * endpoint'а черновик хранился в описаниях файлов заявки (см. git-историю
 * final-decision.lib.ts). Новые черновики живут в /final-decision, но старые строки
 * данных нужно по-прежнему скрывать из «Документов рассмотрения».
 */
export const isFinalDecisionAttachmentDescription = (description?: string | null): boolean =>
  description?.trim() === REJECT_DECISION_ATTACHMENT_DESCRIPTION ||
  description?.trim().startsWith(`${REJECT_DECISION_ATTACHMENT_DESCRIPTION}:`) === true;

type FinalDecisionAuthorNameSource = Pick<FinalDecisionAuthorDto, "lastName" | "firstName" | "middleName">;

/** Автор в формате «Фамилия И. О.» (ФИО Ответственного НТП, снимок из черновика/решения). */
export const formatFinalDecisionAuthorName = (author?: FinalDecisionAuthorNameSource | null): string => {
  const initials = [author?.firstName, author?.middleName]
    .map((part) => part?.trim().charAt(0))
    .filter(Boolean)
    .map((initial) => `${initial?.toUpperCase()}.`)
    .join(" ");

  return [author?.lastName?.trim(), initials].filter(Boolean).join(" ") || "";
};

export const invalidateFinalDecisionVersionQueries = async (queryClient: QueryClient, requestId: string) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: getCurrentVersionQueryKey({ path: { requestId } }) }),
    queryClient.invalidateQueries({ queryKey: getVersionsQueryKey({ path: { requestId } }) }),
  ]);
};

/** Замечания, относящиеся к снимку конкретной исторической версии (до отправки следующей). */
export const filterRemarksForHistoricalVersion = (
  remarks: FinalDecisionRemarkDto[] = [],
  nextVersionSubmittedAt?: string | null,
): FinalDecisionRemarkDto[] =>
  remarks.filter((remark) => {
    if (remark.deletedAt) {
      return false;
    }

    if (!remark.createdAt || !nextVersionSubmittedAt) {
      return true;
    }

    return dayjs(remark.createdAt).isBefore(dayjs(nextVersionSubmittedAt));
  });
